/**
 * Label-raster chunk-diff manifest (v1).
 *
 * The canonical, runtime-neutral wire format for "edits to a chunked zarr label
 * array". Downstream tooling reconstructs the edited mask from the ORIGINAL
 * zarr plus this manifest; see docs/label-raster-manifest-v1.md for the
 * normative specification.
 *
 * Encoding: one CBOR map (RFC 8949) with text keys. `runs` is a CBOR byte
 * string of little-endian int32 triples `(offset, length, value)` addressing
 * the CLIPPED chunk block in row-major order. No CBOR tags are used.
 */
import { decode, encode } from "cbor-x"
import { z } from "zod"

export const LABEL_RASTER_MANIFEST_VERSION = 1 as const
export const LABEL_RASTER_RUN_BYTES = 12 as const

export type LabelRasterDtype = "uint8" | "uint16"

export interface LabelRasterManifestSource {
  seriesId: string
  arrayPath: string
  shape: [number, number]
  chunkShape: [number, number]
  dtype: LabelRasterDtype
  axes: ["y", "x"]
  locator?: string
  taxonomy?: string
  pipelineVersion?: string
}

export interface LabelRasterManifestChunk {
  index: [number, number]
  originalCrc32: number
  runCount: number
  changedPixels: number
  /** Chunk-local, half-open [y0, x0, y1, x1] bounding box of changed pixels. */
  bbox: [number, number, number, number]
  /** `runCount` little-endian int32 triples (offset, length, value). */
  runs: Uint8Array
}

export interface LabelRasterManifestV1 {
  version: typeof LABEL_RASTER_MANIFEST_VERSION
  source: LabelRasterManifestSource
  chunks: LabelRasterManifestChunk[]
  classesUsed: number[]
  editedAt: string
  editor: { app: "vxannotate"; version: string }
}

/**
 * Structural shape of the viewer's in-memory diff (`LabelRasterDiff` in
 * @virdx/wsi-viewer). Declared structurally so shared code stays independent
 * of the viewer package's exports.
 */
export interface LabelRasterDiffLike {
  version: 1
  source: {
    seriesId: string
    arrayPath: string
    shape: [number, number]
    chunkShape: [number, number]
    dtype: LabelRasterDtype
    axes: ["y", "x"]
    locator?: string
    taxonomy?: string
    pipelineVersion?: string
  }
  chunks: Array<{
    index: [number, number]
    originalCrc32: number
    runs: Int32Array
    changedPixels: number
    bbox: [number, number, number, number]
  }>
  classesUsed: number[]
  editedAt: string
}

const dtypeSchema = z.enum(["uint8", "uint16"])
const pairSchema = z.tuple([z.number().int().min(0), z.number().int().min(0)])

export const labelRasterManifestSourceSchema = z.object({
  seriesId: z.string().min(1),
  arrayPath: z.string().min(1),
  shape: pairSchema,
  chunkShape: z.tuple([z.number().int().min(1), z.number().int().min(1)]),
  dtype: dtypeSchema,
  axes: z.tuple([z.literal("y"), z.literal("x")]),
  locator: z.string().optional(),
  taxonomy: z.string().optional(),
  pipelineVersion: z.string().optional()
})

const chunkBytesSchema = z.instanceof(Uint8Array).transform((buf) => new Uint8Array(buf))

export const labelRasterManifestChunkSchema = z
  .object({
    index: pairSchema,
    originalCrc32: z.number().int().min(0).max(0xffffffff),
    runCount: z.number().int().min(0),
    changedPixels: z.number().int().min(0),
    bbox: z.tuple([z.number().int().min(0), z.number().int().min(0), z.number().int().min(0), z.number().int().min(0)]),
    runs: chunkBytesSchema
  })
  .refine((c) => c.runs.byteLength === c.runCount * LABEL_RASTER_RUN_BYTES, {
    message: "runs byte length must equal runCount * 12"
  })

export const labelRasterManifestSchemaV1: z.ZodType<LabelRasterManifestV1, unknown> = z
  .object({
    version: z.literal(LABEL_RASTER_MANIFEST_VERSION),
    source: labelRasterManifestSourceSchema,
    chunks: z.array(labelRasterManifestChunkSchema),
    classesUsed: z.array(z.number().int().min(0)),
    editedAt: z.string().min(1),
    editor: z.object({ app: z.literal("vxannotate"), version: z.string() })
  })
  .superRefine((m, ctx) => {
    const [h, w] = m.source.shape
    const [ch, cw] = m.source.chunkShape
    const chunksY = Math.ceil(h / ch)
    const chunksX = Math.ceil(w / cw)
    const seen = new Set<string>()
    m.chunks.forEach((chunk, i) => {
      const [cy, cx] = chunk.index
      if (cy >= chunksY || cx >= chunksX) {
        ctx.addIssue({ code: "custom", path: ["chunks", i, "index"], message: "chunk index out of range" })
      }
      const key = `${cy},${cx}`
      if (seen.has(key)) {
        ctx.addIssue({ code: "custom", path: ["chunks", i, "index"], message: "duplicate chunk index" })
      }
      seen.add(key)
      const bw = Math.min(cw, w - cx * cw)
      const bh = Math.min(ch, h - cy * ch)
      const pixelCount = Math.max(0, bw) * Math.max(0, bh)
      // Byte-length problems are reported by the chunk schema; skip run checks then.
      if (chunk.runs.byteLength % LABEL_RASTER_RUN_BYTES !== 0) return
      const runs = bytesToRuns(chunk.runs)
      let prevEnd = -1
      let total = 0
      for (let r = 0; r < runs.length; r += 3) {
        const offset = runs[r] as number
        const length = runs[r + 1] as number
        if (length < 1 || offset < prevEnd || offset + length > pixelCount) {
          ctx.addIssue({
            code: "custom",
            path: ["chunks", i, "runs"],
            message: "runs must be sorted, non-overlapping, length >= 1 and within the clipped chunk block"
          })
          break
        }
        prevEnd = offset + length
        total += length
      }
      if (total !== chunk.changedPixels) {
        ctx.addIssue({
          code: "custom",
          path: ["chunks", i, "changedPixels"],
          message: "changedPixels must equal the sum of run lengths"
        })
      }
    })
  }) as unknown as z.ZodType<LabelRasterManifestV1, unknown>

/** Int32Array triples → little-endian bytes (copies). */
export function runsToBytes(runs: Int32Array): Uint8Array {
  const out = new Uint8Array(runs.length * 4)
  const view = new DataView(out.buffer)
  for (let i = 0; i < runs.length; i++) {
    view.setInt32(i * 4, runs[i] as number, true)
  }
  return out
}

/** Little-endian bytes → Int32Array triples (copies into an aligned buffer). */
export function bytesToRuns(bytes: Uint8Array): Int32Array {
  if (bytes.byteLength % 4 !== 0) {
    throw new Error("runs byte length must be a multiple of 4")
  }
  const out = new Int32Array(bytes.byteLength / 4)
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  for (let i = 0; i < out.length; i++) {
    out[i] = view.getInt32(i * 4, true)
  }
  return out
}

export function diffToManifest(
  diff: LabelRasterDiffLike,
  extra?: { locator?: string; editorVersion?: string }
): LabelRasterManifestV1 {
  const source: LabelRasterManifestSource = {
    seriesId: diff.source.seriesId,
    arrayPath: diff.source.arrayPath,
    shape: [diff.source.shape[0], diff.source.shape[1]],
    chunkShape: [diff.source.chunkShape[0], diff.source.chunkShape[1]],
    dtype: diff.source.dtype,
    axes: ["y", "x"]
  }
  const locator = extra?.locator ?? diff.source.locator
  if (locator) source.locator = locator
  if (diff.source.taxonomy) source.taxonomy = diff.source.taxonomy
  if (diff.source.pipelineVersion) source.pipelineVersion = diff.source.pipelineVersion

  return {
    version: LABEL_RASTER_MANIFEST_VERSION,
    source,
    chunks: diff.chunks
      .filter((c) => c.runs.length > 0)
      .map((c) => ({
        index: [c.index[0], c.index[1]],
        originalCrc32: c.originalCrc32 >>> 0,
        runCount: c.runs.length / 3,
        changedPixels: c.changedPixels,
        bbox: [c.bbox[0], c.bbox[1], c.bbox[2], c.bbox[3]],
        runs: runsToBytes(c.runs)
      })),
    classesUsed: [...diff.classesUsed].sort((a, b) => a - b),
    editedAt: diff.editedAt,
    editor: { app: "vxannotate", version: extra?.editorVersion ?? "dev" }
  }
}

export function manifestToDiff(manifest: LabelRasterManifestV1): LabelRasterDiffLike {
  return {
    version: 1,
    source: { ...manifest.source, axes: ["y", "x"] },
    chunks: manifest.chunks.map((c) => ({
      index: [c.index[0], c.index[1]],
      originalCrc32: c.originalCrc32,
      runs: bytesToRuns(c.runs),
      changedPixels: c.changedPixels,
      bbox: [c.bbox[0], c.bbox[1], c.bbox[2], c.bbox[3]]
    })),
    classesUsed: [...manifest.classesUsed],
    editedAt: manifest.editedAt
  }
}

export function encodeLabelRasterManifest(manifest: LabelRasterManifestV1): Uint8Array {
  return encode(manifest)
}

/** Decode + validate; returns null on any structural problem. */
export function decodeLabelRasterManifest(bytes: Uint8Array): LabelRasterManifestV1 | null {
  try {
    const raw = decode(bytes)
    const parsed = labelRasterManifestSchemaV1.safeParse(raw)
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

/** JSON-transport form (REST API): identical shape but `runs` is base64. */
export interface LabelRasterManifestJson extends Omit<LabelRasterManifestV1, "chunks"> {
  chunks: Array<Omit<LabelRasterManifestChunk, "runs"> & { runs: string }>
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = ""
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(bin)
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function manifestToJson(manifest: LabelRasterManifestV1): LabelRasterManifestJson {
  return { ...manifest, chunks: manifest.chunks.map((c) => ({ ...c, runs: bytesToBase64(c.runs) })) }
}

export function manifestFromJson(json: LabelRasterManifestJson): LabelRasterManifestV1 {
  return { ...json, chunks: json.chunks.map((c) => ({ ...c, runs: base64ToBytes(c.runs) })) }
}

/** Sum of run bytes across chunks (for size warnings). */
export function manifestRunBytes(manifest: Pick<LabelRasterManifestV1, "chunks">): number {
  return manifest.chunks.reduce((n, c) => n + c.runs.byteLength, 0)
}
